/**
 * IconButton — a square Button with a mandatory accessible name.
 *
 * `label` is not optional on purpose: an icon-only control without a name is an
 * automatic reject. It becomes both the `aria-label` and, by default, the
 * tooltip copy — `tooltipContent` overrides the second for the few controls
 * whose hover has something to add beyond the name.
 */

import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Spinner } from './Button';
import type { ButtonVariant, ControlSize } from './Button';
import { Tooltip } from './Tooltip';

const BASE = [
  'relative inline-grid shrink-0 place-items-center select-none',
  'rounded-sm border leading-none',
  'transition-[background-color,border-color,color,box-shadow,opacity]',
  'duration-[var(--dur-fast)] ease-[var(--ease-out)]',
  '[&_svg]:shrink-0',
].join(' ');

const SIZE: Record<ControlSize, string> = {
  sm: 'size-[var(--h-control-sm)] [&_svg]:size-3.5',
  md: 'size-[var(--h-control)] [&_svg]:size-4',
  lg: 'size-[var(--h-control-lg)] [&_svg]:size-[18px]',
};

const DISABLED_FILLED = [
  'disabled:cursor-not-allowed disabled:shadow-none',
  'disabled:bg-surface-3 disabled:hover:bg-surface-3',
  'disabled:text-muted disabled:hover:text-muted',
  'disabled:border-transparent disabled:hover:border-transparent',
  'disabled:opacity-100 disabled:hover:opacity-100',
].join(' ');

const DISABLED_QUIET = [
  'disabled:cursor-not-allowed disabled:shadow-none',
  'disabled:bg-transparent disabled:hover:bg-transparent',
  'disabled:text-muted disabled:hover:text-muted',
  'disabled:border-transparent disabled:hover:border-transparent',
  'disabled:opacity-100 disabled:hover:opacity-100',
].join(' ');

const VARIANT: Record<ButtonVariant, string> = {
  primary: cn(
    'border-transparent bg-accent text-on-accent shadow-1 hover:bg-accent-hover active:opacity-90',
    DISABLED_FILLED,
  ),
  secondary: cn(
    'border-line bg-surface-2 text-secondary shadow-1',
    'hover:border-line-strong hover:bg-surface-3 hover:text-primary active:bg-active',
    DISABLED_FILLED,
  ),
  outline: cn(
    'border-line-strong bg-transparent text-secondary hover:bg-hover hover:text-primary active:bg-active',
    DISABLED_QUIET,
  ),
  ghost: cn(
    'border-transparent bg-transparent text-muted',
    'hover:border-line hover:bg-hover hover:text-primary active:bg-active',
    DISABLED_QUIET,
  ),
  subtle: cn(
    'border-transparent bg-hover text-secondary hover:bg-active hover:text-primary active:bg-active',
    DISABLED_QUIET,
  ),
  danger: cn(
    'border-transparent bg-transparent text-muted',
    'hover:border-[var(--danger)] hover:bg-[var(--danger-soft)] hover:text-danger active:bg-[var(--danger-soft)]',
    DISABLED_QUIET,
  ),
};

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Required. Becomes the `aria-label` and the tooltip body. */
  label: string;
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ControlSize;
  loading?: boolean;
  /** Set false inside menus and toolbars that already narrate their items. */
  tooltip?: boolean;
  /**
   * Tooltip body, when it should say more than the name.
   *
   * Defaults to `label`, which is right for the overwhelming majority: a
   * tooltip that repeats the name is the whole point when the name is the only
   * thing the icon fails to say. It is wrong for a control whose *value* is
   * invisible — the theme trigger's icon already draws "Switch appearance", so
   * repeating it teaches a hovering reader nothing, while `Appearance —
   * follows the system` tells them the state they cannot otherwise see without
   * opening the menu. The accessible name stays `label` either way.
   */
  tooltipContent?: ReactNode;
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
  /** Round it off — used for avatars and dismiss affordances on chips. */
  round?: boolean;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    label,
    children,
    variant = 'ghost',
    size = 'md',
    loading = false,
    tooltip = true,
    tooltipContent,
    tooltipSide = 'top',
    round = false,
    className,
    disabled,
    type,
    ...rest
  },
  ref,
) {
  const isDisabled = disabled === true || loading;

  const button = (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-label={label}
      aria-busy={loading || undefined}
      disabled={isDisabled}
      className={cn(BASE, SIZE[size], VARIANT[variant], className)}
      // Inline so the global `:focus-visible` radius cannot square off a pill.
      style={round ? { borderRadius: 'var(--radius-full)' } : undefined}
      {...rest}
    >
      {/* The glyph is the button's picture of its own `label`, never a second
          thing to announce. `display: contents` keeps the box model exactly as
          it was; `aria-hidden` keeps a lucide `<svg>` — which carries no
          `aria-hidden` of its own — from reaching the tree as an unnamed `img`
          node inside an already-named control. */}
      <span aria-hidden="true" className="contents">
        {loading ? <Spinner /> : children}
      </span>
    </button>
  );

  if (!tooltip || isDisabled) return button;
  return (
    <Tooltip content={tooltipContent ?? label} side={tooltipSide}>
      {button}
    </Tooltip>
  );
});
